/**
 * Positions meeting tiles on the stage: a gallery grid whose cards fill the stage, or one
 * large tile with a strip of others along the top (paged when they do not fit), plus a floating
 * self-view.
 *
 * Every tile is rendered once and moved with absolute positioning, so changing the view
 * never moves a <video> element between containers (which would force Chime to rebind it).
 */
export type LayoutMode = "gallery" | "speaker" | "content";

export interface TileRect {
  x: number;
  y: number;
  w: number;
  h: number;
  hidden: boolean;
  floating: boolean;
  main: boolean;
  /** One of the small tiles in the strip above the large tile. */
  strip: boolean;
}

export interface LayoutInput {
  width: number;
  height: number;
  /** Tile ids in display order. The shared-content tile, when present, is one of them. */
  ids: string[];
  mode: LayoutMode;
  /** The large tile in speaker or content mode. */
  mainId: string | null;
  /** The local person's tile, floated in the corner when `selfFloating` is set. */
  selfId: string | null;
  selfFloating: boolean;
  /** Hides the self tile, floating or in the grid, as long as someone else is on the stage. */
  selfHidden: boolean;
  compact: boolean;
  /**
   * The room's size tier as a factor of the design sizes: 1, 0.85 on shorter or narrower
   * screens and 0.75 on short ones (see `sizeScale` in the room). Scales gaps and the self tile.
   */
  scale?: number;
  /** The strip's page when the others do not fit in one row; clamped to the last page. */
  stripPage?: number;
}

export interface LayoutResult {
  rects: { [id: string]: TileRect };
  /** The gallery's "+N more" card. */
  overflow: { rect: TileRect; count: number } | null;
  /** The strip's ‹ 1/2 › control, at the end of the row, when the others take more than one page. */
  pager: { rect: TileRect; page: number; pages: number } | null;
}

const GALLERY_MAX = 49;
/** Room at the end of the strip for the page control (two 32px buttons and the count; not scaled). */
const PAGER_WIDTH = 104;
/**
 * Gallery cards stretch to fill the stage, but never past these shapes (width / height). A lone
 * card on a short, wide window (1536 x 730 gives about 2.85) should still fill the stage.
 */
const CARD_MIN_ASPECT = 0.75;
const CARD_MAX_ASPECT = 3;

export function computeLayout(input: LayoutInput): LayoutResult {
  const rects: { [id: string]: TileRect } = {};
  const width = Math.max(0, input.width);
  const height = Math.max(0, input.height);
  const scale = input.scale || 1;
  const gap = input.compact ? 8 : Math.round(12 * scale);
  let overflow: LayoutResult["overflow"] = null;
  let pager: LayoutResult["pager"] = null;

  let ids = input.ids;
  const hasSelf = !!input.selfId && ids.includes(input.selfId);
  if (hasSelf && input.selfFloating) {
    ids = ids.filter((id) => id !== input.selfId);
    rects[input.selfId as string] = selfRect(width, height, input.compact, input.selfHidden, scale);
  } else if (hasSelf && input.selfHidden && ids.length > 1) {
    ids = ids.filter((id) => id !== input.selfId);
    rects[input.selfId as string] = hiddenRect();
  }

  const mainId = input.mode !== "gallery" && input.mainId && ids.includes(input.mainId) ? input.mainId : null;

  if (!mainId) {
    let visible = ids;
    let hiddenCount = 0;
    if (ids.length > GALLERY_MAX) {
      visible = ids.slice(0, GALLERY_MAX - 1);
      hiddenCount = ids.length - visible.length;
    }
    const slots = hiddenCount ? visible.length + 1 : visible.length;
    // A few large cards get the design's wide gutter; a crowded grid keeps more room for faces.
    const gridGap = input.compact ? gap : Math.round((slots <= 4 ? 24 : 16) * scale);
    // Cards fill their cells, so the grid is chosen for 4:3 cards rather than 16:9 video.
    const cells = gridCells(slots, width, height, gridGap, width >= height ? 4 / 3 : 3 / 4);
    visible.forEach((id, index) => (rects[id] = cells[index]));
    if (hiddenCount) overflow = { rect: cells[visible.length], count: hiddenCount };
    ids.slice(visible.length).forEach((id) => (rects[id] = hiddenRect()));
    return { rects, overflow, pager };
  }

  const others = ids.filter((id) => id !== mainId);
  let stripHeight = 0;
  if (others.length) {
    stripHeight = input.compact ? 64 : Math.round(Math.min(132 * scale, Math.max(80 * scale, height * 0.17)));
    const tileWidth = Math.round((stripHeight * 16) / 9);
    let perPage = Math.max(1, Math.floor((width + gap) / (tileWidth + gap)));
    let pagerWidth = 0;
    if (others.length > perPage) {
      pagerWidth = PAGER_WIDTH;
      perPage = Math.max(1, Math.floor((width - pagerWidth) / (tileWidth + gap)));
    }
    const pages = Math.ceil(others.length / perPage);
    const page = Math.min(Math.max(0, input.stripPage || 0), pages - 1);
    // Every page is laid out as a full one, so a short last page does not move the page control.
    const count = Math.min(others.length, perPage);
    const rowWidth = count * tileWidth + (count - 1) * gap + (pagerWidth ? gap + pagerWidth : 0);
    const left = Math.max(0, (width - rowWidth) / 2);
    const place = (index: number, hidden: boolean): TileRect => ({
      x: left + index * (tileWidth + gap),
      y: 0,
      w: tileWidth,
      h: stripHeight,
      hidden,
      floating: false,
      main: false,
      strip: true,
    });
    // Tiles on other pages keep their slot, so turning a page fades them in place.
    others.forEach((id, index) => (rects[id] = place(index % perPage, Math.floor(index / perPage) !== page)));
    if (pages > 1) pager = { rect: { ...place(count, false), w: pagerWidth, strip: false }, page, pages };
  }

  const top = stripHeight ? stripHeight + gap : 0;
  const areaHeight = Math.max(0, height - top);
  if (input.mode === "content") {
    rects[mainId] = { x: 0, y: top, w: width, h: areaHeight, hidden: false, floating: false, main: true, strip: false };
  } else {
    const fitted = fit(width, areaHeight, 16 / 9);
    rects[mainId] = {
      x: (width - fitted.w) / 2,
      y: top + (areaHeight - fitted.h) / 2,
      w: fitted.w,
      h: fitted.h,
      hidden: false,
      floating: false,
      main: true,
      strip: false,
    };
  }
  return { rects, overflow, pager };
}

/**
 * Picks the column count that gives the largest tiles at `aspect`, then stretches the cells to
 * fill the stage (within the card aspect limits) and centres each row.
 */
function gridCells(count: number, width: number, height: number, gap: number, aspect: number): TileRect[] {
  if (!count) return [];
  let best = { cols: 1, rows: count, w: 0, h: 0 };
  for (let cols = 1; cols <= count; cols++) {
    const rows = Math.ceil(count / cols);
    let w = (width - gap * (cols - 1)) / cols;
    let h = w / aspect;
    if (h * rows + gap * (rows - 1) > height) {
      h = (height - gap * (rows - 1)) / rows;
      w = h * aspect;
    }
    if (w > best.w) best = { cols, rows, w, h };
  }
  let fillW = Math.max(0, (width - gap * (best.cols - 1)) / best.cols);
  let fillH = Math.max(0, (height - gap * (best.rows - 1)) / best.rows);
  if (fillH && fillW / fillH > CARD_MAX_ASPECT) fillW = fillH * CARD_MAX_ASPECT;
  if (fillH && fillW / fillH < CARD_MIN_ASPECT) fillH = fillW / CARD_MIN_ASPECT;
  best = { ...best, w: fillW, h: fillH };
  const cells: TileRect[] = [];
  const gridHeight = best.rows * best.h + gap * (best.rows - 1);
  const top = Math.max(0, (height - gridHeight) / 2);
  for (let row = 0; row < best.rows; row++) {
    const inRow = row === best.rows - 1 ? count - best.cols * (best.rows - 1) : best.cols;
    const rowWidth = inRow * best.w + gap * (inRow - 1);
    const left = Math.max(0, (width - rowWidth) / 2);
    for (let col = 0; col < inRow; col++) {
      cells.push({
        x: left + col * (best.w + gap),
        y: top + row * (best.h + gap),
        w: best.w,
        h: best.h,
        hidden: false,
        floating: false,
        main: false,
        strip: false,
      });
    }
  }
  return cells;
}

function fit(width: number, height: number, aspect: number): { w: number; h: number } {
  let w = width;
  let h = w / aspect;
  if (h > height) {
    h = height;
    w = h * aspect;
  }
  return { w, h };
}

function selfRect(width: number, height: number, compact: boolean, hidden: boolean, scale: number): TileRect {
  const portrait = height > width;
  const w = compact ? 96 : Math.round(208 * scale);
  const h = compact && portrait ? 128 : Math.round((w * 9) / 16);
  const margin = compact ? 8 : Math.round(16 * scale);
  return {
    x: Math.max(0, width - w - margin),
    y: Math.max(0, height - h - margin),
    w,
    h,
    hidden,
    floating: true,
    main: false,
    strip: false,
  };
}

function hiddenRect(): TileRect {
  return { x: 0, y: 0, w: 0, h: 0, hidden: true, floating: false, main: false, strip: false };
}

export function tileStyle(rect: TileRect | undefined): { [key: string]: string } {
  if (!rect) return { display: "none" };
  return {
    transform: `translate(${Math.round(rect.x)}px, ${Math.round(rect.y)}px)`,
    width: `${Math.round(rect.w)}px`,
    height: `${Math.round(rect.h)}px`,
  };
}
